import {
  inject,
  Injectable,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { from, Observable, ReplaySubject } from 'rxjs';
import { share, switchMap, take, throwIfEmpty } from 'rxjs/operators';
import {
  addRootContainerIfNecessary,
  components,
  foxitLibPath,
  FoxitModule,
  FoxitModuleToken, foxitPrintWindow,
  PDFFileOptions,
  PDFUI,
  PDFUIOptions,
  PDFViewCtrl,
} from 'webcommon/lazy-load-commonjs/foxit';
import { BrowserService } from 'webcommon/legacy-common';

import {
  ApBaseClass,
  FoxitLicenseDto,
  FoxitLicenseDtoToken, patchObjectFn, throwSyncError,
} from 'webcommon/shared';

import { pdfMimeType } from '../../services';
import { IPdfRenderer } from './IPdfRenderer';

// todo: make changes based on best practices from docs:
//  - tiling size
//  - zoom
//  - rendering mode
//  - loading
// todo: add webpack special comments for foxit dynamic import?
// todo: compose the ap-enhanced-document-viewer with a foxit element for pdf signing as ng-content or a template,
//  in the places it's necessary, instead of that being the default
// todo: try addon-loader package to see if it's useful for our setup
@Injectable()
export class FoxitPdfRendererBase extends ApBaseClass implements IPdfRenderer, OnDestroy {
  private readonly zone = inject(NgZone);
  protected readonly browserService = inject(BrowserService);
  protected foxitModule: FoxitModule = inject(FoxitModuleToken);
  protected foxitSetup: FoxitLicenseDto = this.createConfig(inject(FoxitLicenseDtoToken));
  protected element = inject(HTMLElement);

  private _destroyed = false;
  private redrawTimeoutId?: ReturnType<typeof setTimeout>;
  protected readonly pdfUI: PDFUI;
  private readonly _afterMounted$ = new ReplaySubject<components.Component>(1);
  get afterMounted$(): Observable<components.Component> {
    return this._afterMounted$;
  }

  // emits an event after each time that foxit (iframe-window | popup-window).print() is done
  // normally, you would want to reference getAfterPrintedP instead of this
  private readonly afterWindowPrint$ = new Observable<foxitPrintWindow>(subscriber => {
    return from(this.pdfUI.getPDFViewer()).pipe(
      take(1),
      switchMap(value => this.getPrintStream$(value)),
    ).subscribe(subscriber);
  }).pipe(
    share()
  );

  /**
   * Returns undefined so that subclasses can override and supply a custom Foxit UI template.
   * Keeping this as a separate overridable method avoids coupling template concerns to getOptions().
   */
  protected getNewTemplate(): PDFUIOptions['template'] {
    return undefined;
  }

  /**
   * Provides a hook for subclasses to transform or augment the injected license config
   * before it is stored and used to build PDFUI options.
   */
  protected createConfig(
    foxitSetup: FoxitLicenseDto,
  ): FoxitLicenseDto {
    return foxitSetup;
  }

  // element: HTMLElement to mount the pdf viewer on
  protected getOptions(
    foxitModule: FoxitModule,
    foxitSetup: FoxitLicenseDto,
    element: HTMLElement,
  ): PDFUIOptions {
    const [_container, containerViewerUI] = addRootContainerIfNecessary(foxitModule);

    return {
      addons: [],
      customs: { defaultStateHandler: foxitSetup.DefaultTool },
      fragments: [],
      renderTo: element,
      viewerOptions: {
        // If we want to reference stuff from 'external' folder in foxit package (like in the examples),
        // then that needs to be setup to be copied to somewhere in the output directory,
        // just like the 'lib' folder currently is.
        // Currently, we aren't referencing anything in that folder.
        jr: {
          licenseKey: foxitSetup.LicenseKey,
          licenseSN: foxitSetup.LicenseSN,
        },
        libPath: foxitLibPath,
        viewerUI: containerViewerUI,
      },
      template: this.getNewTemplate(),
      // We were previously using the default RibbonAppearance, so this is just applying some overrides to that.
      // TODO: do we need to use AdaptiveAppearance or some other calculation to toggle between RibbonAppearance and MobileAppearance
      //  for a more responsive look???
      appearance: foxitModule.appearances.RibbonAppearance.extend({
        afterMounted: (root) => {
          this._afterMounted$.next(root);
        },
      }),
    };
  }

  /**
   * Assembles options from the current instance state and delegates to _createPDFUI.
   * Kept separate from _createPDFUI so subclasses can override option assembly
   * without having to replicate the event-listener wiring in _createPDFUI.
   */
  protected createPDFUI(): PDFUI {
    return this._createPDFUI(
      this.foxitModule,
      this.foxitSetup,
      this.getOptions(
        this.foxitModule,
        this.foxitSetup,
        this.element,
      ),
    );
  }

  /**
   * Instantiates PDFUI and wires the viewer event listeners that drive redraw scheduling.
   * Redraw is intentionally deferred via scheduleRedraw() rather than called immediately,
   * because Foxit may still be finalizing internal state when these events fire.
   * Listener teardowns are collected so they can all be removed atomically on destroy.
   */
  private _createPDFUI(
    foxitModule: FoxitModule,
    foxitSetup: FoxitLicenseDto,
    options: PDFUIOptions,
  ): PDFUI {
    const pdfUI = new foxitModule.PDFUI(options);
    pdfUI.addViewerEventListener('render-file-success', async (pdfDoc) => {
      const pageCount = pdfDoc.getPageCount();
      if (pageCount === 1) {
        // if there is 1 page, then currently, we want to disable the single-page-button,
        // change to 'continuous-view-mode'
        // I guess the reason is that the scrolling doesn't work well with single-page, or is broken for two finger scrolling on touchpad
        // from #81855
        const singlePageButton = await pdfUI.getComponentByName('single-page');
        singlePageButton?.disable();

        const viewModeMgr = await pdfUI.getViewModeManager();
        viewModeMgr.switchTo('continuous-view-mode');
      } else {
        // else, use the configured default ViewMode
        const viewModeMgr = await pdfUI.getViewModeManager();
        viewModeMgr.switchTo(foxitSetup.DefaultViewMode);
      }
    });
    return pdfUI;
  }

  // get a promise that resolves after the root PDFUI component mounted into DOM tree.
  getAfterMountedP(): Promise<components.Component> {
    // some kind of 'take' operator is necessary here,
    // because afterMounted$ never completes until ngOnDestroy,
    // and the Promise won't resolve until the Observable completes
    return this.afterMounted$.pipe(
      throwIfEmpty(() => new Error('FoxitPdfRendererBase: a value was never emitted from afterMounted$ before complete()')),
      take(1),
    ).toPromise();
  }

  // get a promise that resolves after a print is done
  // WARNING: this will never resolve if print() is not called (manually or from print-button)
  getAfterPrintedP(): Promise<foxitPrintWindow> {
    // some kind of 'take' operator is necessary here,
    // because afterWindowPrint$ never definitely completes,
    // and the Promise won't resolve until the Observable completes
    return this.afterWindowPrint$.pipe(
      throwIfEmpty(() => new Error('FoxitPdfRendererBase: a value was never emitted from afterWindowPrint$ before complete()')),
      take(1),
    ).toPromise();
  }

  // search for "windowPrint" in UIExtension.full.js to see what function this method referring to
  // IMPORTANT: you can only get this stream once for this pdfViewer, because we only want to patch the function once
  private getPrintStream$(pdfViewer: PDFViewCtrl.PDFViewer): Observable<foxitPrintWindow> {
    return new Observable<foxitPrintWindow>(subscriber => {
      if (!pdfViewer.printer) {
        // eslint-disable-next-line max-len
        subscriber.error(new Error('undefined pdfViewer.printer; print() must be called (manually or from print-button) before getting the print stream'));
        return;
      }
      // return the result of patchObjectFn, so that unpatching can be part of the teardown from unsubscribe
      return patchObjectFn(pdfViewer.printer.printArea, 'windowPrint', (originalFn) => {
        return (e) => {
          originalFn(e);
          // at this point, the window.print() is done
          // it may also be possible to use the afterprint event on e.win
          subscriber.next(e);
        };
      });
    });
  }

  // IMPORTANT: this returned promise from ngOnDestroy is not waited on when angular calls it automatically from lifecycle
  override async ngOnDestroy(): Promise<void> {
    // don't allow multiple destroys, since PDFUI.destroy() can't handle it
    // similar to how Injectors work, but without the thrown Error:
    // https://github.com/angular/angular/blob/15.2.1/packages/core/src/di/r3_injector.ts#L192-L195
    if (this._destroyed) {
      return;
    }
    this._destroyed = true;

    // run the super.ngOnDestroy first to trigger the onDestroy$, and allow PdfRendererFactory
    // to do what it needs to do
    super.ngOnDestroy();
    if (this.redrawTimeoutId) {
      clearTimeout(this.redrawTimeoutId);
      this.redrawTimeoutId = undefined;
    }
    this._afterMounted$.complete();
    await this.destroyViewer(this.pdfUI);
  }

  /**
   * Convenience wrapper that converts a base64 string to a Blob before opening,
   * keeping callers from needing to know the PDF MIME type or the blob conversion API.
   */
  async openPDFData(base64String: string, options?: PDFFileOptions): Promise<void> {
    const blob = this.browserService.base64ToBlob(base64String, pdfMimeType);
    await this.openPDFDataBlob(blob, options);
  }

  async openPDFDataBlob(blob: Blob, options?: PDFFileOptions): Promise<void> {
    await this.pdfUI.openPDFByFile(blob, options);
  }

  /**
   * Debounces calls to redraw() so that rapid sequential events (e.g. zoom + view-mode change
   * triggered together) collapse into a single repaint, avoiding redundant Foxit render passes.
   */
  scheduleRedraw(force = false): void {
    // I guess this is kinda based on prototype.redrawViewer in UIExtension.full.js
    if (this.redrawTimeoutId) {
      clearTimeout(this.redrawTimeoutId);
    }
    this.redrawTimeoutId = setTimeout(async () => {
      // be aware, any errors from this call will be global because of being inside this setTimeout
      await this.redraw(force);
    }, 50);
  }

  /**
   * Guards against calling pdfUI.redraw() before the viewer is mounted,
   * which would be a no-op or throw internally.
   */
  async redraw(force = false): Promise<void> {
    await this.getAfterMountedP();
    await this.pdfUI.redraw(force);
  }

  // for ObjectUrl, use a call like this instead:
  //  pdfViewer.openPDFByHttpRangeRequest({
  //  range: {
  //      url: 'http://10.103.4.154:9898/_testfile/big/performance-diffSize/Test_1GB.pdf',
  //    }
  //  }, {fileName: 'fileName.pdf'});
  async openPDFUrl(...args: Parameters<PDFUI['openPDFByHttpRangeRequest']>): Promise<void> {
    await this.pdfUI.openPDFByHttpRangeRequest(...args);
  }

  /**
   * Foxit's destroy() triggers DOM mutations and internal cleanup that should not
   * cause Angular change detection cycles, so it is explicitly run outside the zone.
   */
  private async destroyViewer(pdfViewer: PDFUI | null): Promise<void> {
    if (pdfViewer) {
      await this.zone.runOutsideAngular(async () => {
        await pdfViewer.destroy();
      });
    }
  }

  /**
   * getComponentByName() returns null when the component isn't found, which would silently
   * produce a null reference error later. Throwing here surfaces the problem at the call site.
   */
  async getComponentOrThrow<TComponent extends components.Component>(name: string): Promise<TComponent> {
    return (await this.pdfUI.getComponentByName<TComponent>(name)) || throwSyncError(new Error(`could not find component: '${name}'`));
  }

  async getPageCount(): Promise<number> {
    return (await this.pdfUI.getCurrentPDFDoc())?.getPageCount() || 0;
  }
}
